/**
 * ProfilePage v2 — pagina root "Profilo".
 *
 * Replica del mobile `Profile.js`:
 *  1. Header "Profilo"
 *  2. Avatar grande + nome utente + codice cliente
 *  3. Card "Informazioni personali" (email + telefono) con edit dialog
 *  4. Sezione "Le tue forniture" (lista contratti propri + monitoraggio)
 *  5. Sezione "Assistenza" (call + whatsapp)
 *  6. Sezione "Sicurezza" (link a /profilo/sicurezza, placeholder)
 *  7. Bottone "Esci" rosso outline
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  ChevronRight,
  LogOut,
  Mail,
  MessageCircle,
  Pencil,
  Phone,
  PhoneCall,
  ShieldCheck,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, getInitials } from '@/components/ui/avatar'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { logout, getDisplayName } from '@/features/auth/api/auth'
import { useAuthStore } from '@/features/auth/authStore'
import { useActiveContract } from '@/features/auth/hooks/useActiveContract'
import { ENERMED_PHONE, whatsappUrl } from '@/features/assistance/contacts'
import { ContractMiniCard } from './components/ContractMiniCard'
import { ProfileEditDialog } from './components/ProfileEditDialog'

type EditMode = 'email' | 'phone' | null

export function ProfilePage() {
  const navigate = useNavigate()
  const reset = useAuthStore((s) => s.reset)
  const user = useAuthStore((s) => s.user)
  const activeContractCode = useAuthStore((s) => s.activeContractCode)
  const {
    cliente,
    contratti,
    monitoraggio,
    isLoading,
    error,
  } = useActiveContract()

  const [editMode, setEditMode] = useState<EditMode>(null)
  const [logoutOpen, setLogoutOpen] = useState(false)
  const [logoutPending, setLogoutPending] = useState(false)

  const displayName = cliente?.nome_completo ?? (user ? getDisplayName(user) : '—')
  const codiceCliente = cliente?.codice_cliente ?? user?.codiceCliente ?? ''
  const email = cliente?.email ?? user?.email ?? ''
  const phone = cliente?.phone ?? user?.phone ?? ''

  async function onLogoutConfirm() {
    setLogoutPending(true)
    try {
      await logout()
      reset()
      navigate('/login', { replace: true })
    } catch {
      setLogoutPending(false)
    }
  }

  if (error) {
    return (
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        <Header />
        <EmptyState
          icon={<AlertCircle className="h-5 w-5 text-destructive" />}
          title="Non siamo riusciti a caricare il profilo"
          description={error.message}
        />
      </div>
    )
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <Header />

      {/* Avatar + nome */}
      <section className="flex flex-col items-center gap-3 text-center">
        <Avatar className="h-24 w-24">
          <AvatarFallback className="text-2xl">{getInitials(displayName)}</AvatarFallback>
        </Avatar>
        {isLoading ? (
          <Skeleton className="h-6 w-48" />
        ) : (
          <h2 className="text-xl font-bold text-foreground">{displayName}</h2>
        )}
        {codiceCliente && (
          <p className="text-sm text-muted-foreground">
            Codice cliente: <span className="font-mono text-foreground">{codiceCliente}</span>
          </p>
        )}
      </section>

      {/* Informazioni personali */}
      <section className="flex flex-col gap-2">
        <h3 className="text-base font-bold text-foreground">Informazioni personali</h3>
        <Card>
          <CardContent className="flex flex-col p-0">
            <InfoRow
              icon={<Mail className="h-4 w-4 text-secondary" />}
              label="Email"
              value={email}
              onEdit={() => setEditMode('email')}
              loading={isLoading}
            />
            <div className="h-px bg-border" />
            <InfoRow
              icon={<Phone className="h-4 w-4 text-secondary" />}
              label="Telefono"
              value={phone}
              onEdit={() => setEditMode('phone')}
              loading={isLoading}
            />
          </CardContent>
        </Card>
      </section>

      {/* Le tue forniture */}
      <section className="flex flex-col gap-2">
        <h3 className="text-base font-bold text-foreground">Le tue forniture</h3>
        {isLoading ? (
          <ContractsSkeleton />
        ) : contratti.length === 0 && monitoraggio.length === 0 ? (
          <Card className="p-6 text-center">
            <p className="text-sm text-muted-foreground">Non hai forniture associate.</p>
          </Card>
        ) : (
          <div className="flex flex-col gap-2">
            {contratti.map((c) => (
              <ContractMiniCard
                key={c.codice_contratto}
                contract={c}
                isActive={c.codice_contratto === activeContractCode}
              />
            ))}
            {monitoraggio.length > 0 && (
              <>
                <h4 className="mt-2 text-xs uppercase tracking-wider text-muted-foreground">
                  Monitoraggio
                </h4>
                {monitoraggio.map((c) => (
                  <ContractMiniCard
                    key={c.codice_contratto}
                    contract={c}
                    isActive={c.codice_contratto === activeContractCode}
                    isMonitored
                  />
                ))}
              </>
            )}
          </div>
        )}
      </section>

      {/* Assistenza */}
      <section className="flex flex-col gap-2">
        <h3 className="text-base font-bold text-foreground">Assistenza</h3>
        <div className="grid grid-cols-2 gap-3">
          <a
            href={`tel:${ENERMED_PHONE}`}
            className="block"
          >
            <Card interactive className="flex flex-col items-center gap-2 p-5">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-success/15 text-success">
                <PhoneCall className="h-5 w-5" />
              </div>
              <p className="text-sm font-semibold text-foreground">Chiama</p>
              <p className="text-xs text-muted-foreground">{ENERMED_PHONE}</p>
            </Card>
          </a>
          <a
            href={whatsappUrl()}
            target="_blank"
            rel="noreferrer"
            className="block"
          >
            <Card interactive className="flex flex-col items-center gap-2 p-5">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#25D366]/20 text-[#25D366]">
                <MessageCircle className="h-5 w-5" />
              </div>
              <p className="text-sm font-semibold text-foreground">Scrivici</p>
              <p className="text-xs text-muted-foreground">WhatsApp</p>
            </Card>
          </a>
        </div>
      </section>

      {/* Sicurezza */}
      <section className="flex flex-col gap-2">
        <h3 className="text-base font-bold text-foreground">Sicurezza</h3>
        <Link to="/profilo/sicurezza" className="block">
          <Card interactive className="flex items-center gap-3 p-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary/15 text-secondary">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground">
                Autenticazione a due fattori
              </p>
              <p className="text-xs text-muted-foreground">
                Gestisci MFA via SMS o email
              </p>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </Card>
        </Link>
      </section>

      {/* Esci */}
      <Button
        variant="outline"
        className="mt-2 gap-2 border-destructive/40 bg-destructive/5 text-destructive hover:bg-destructive/10"
        onClick={() => setLogoutOpen(true)}
      >
        <LogOut className="h-4 w-4" />
        Esci
      </Button>

      {/* Edit dialog */}
      {editMode && codiceCliente && (
        <ProfileEditDialog
          open={editMode !== null}
          onOpenChange={(o) => !o && setEditMode(null)}
          mode={editMode}
          codiceCliente={codiceCliente}
        />
      )}

      {/* Logout confirm */}
      <Dialog open={logoutOpen} onOpenChange={(o) => !logoutPending && setLogoutOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-warning/20 text-warning-foreground">
              <LogOut className="h-5 w-5" />
            </div>
            <DialogTitle>Esci dall’app</DialogTitle>
            <DialogDescription>Vuoi davvero uscire dal tuo account?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={logoutPending}>
                Annulla
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={logoutPending}
              onClick={onLogoutConfirm}
            >
              Esci
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function Header() {
  return (
    <header>
      <h1 className="text-2xl font-bold text-foreground md:text-3xl">Profilo</h1>
      <p className="text-sm text-muted-foreground">
        Gestisci i tuoi dati, le forniture e l’assistenza.
      </p>
    </header>
  )
}

function InfoRow({
  icon,
  label,
  value,
  onEdit,
  loading,
}: {
  icon: React.ReactNode
  label: string
  value: string
  onEdit: () => void
  loading?: boolean
}) {
  return (
    <div className="flex items-center gap-3 px-5 py-4">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary/15">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        {loading ? (
          <Skeleton className="mt-1 h-4 w-40" />
        ) : (
          <p className="truncate text-sm font-medium text-foreground">
            {value || '—'}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onEdit}
        aria-label={`Modifica ${label.toLowerCase()}`}
        className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Pencil className="h-4 w-4" />
      </button>
    </div>
  )
}

function ContractsSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 2 }).map((_, i) => (
        <Card key={i} className="flex items-start gap-3 p-4">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-56" />
            <Skeleton className="h-3 w-32" />
          </div>
        </Card>
      ))}
    </div>
  )
}

